import {
  getFilteredLearners,
  LearnerAndInviteeResponse,
  PaginatedResponse,
  usePaginatedApi,
} from 'api';
import { queryStatusIgnoreIdle } from 'utils/combineQueryStatus';
import {
  DefaultPageParam,
  getMinimumSearchCharacters,
  type QueryParamsBase,
  SearchMode,
} from 'api/search';
import { QueryStatus } from '@tanstack/react-query';
import usePaginationV2, {
  DEFAULT_TABLE_ITEMS_PER_PAGE,
  Paginator,
} from 'components/hooks/usePaginationV2';
import { QUERY_KEY_ROOTS } from 'variables';
import { ApiPaginationProps } from 'components/Pagination/Pagination';
import {
  TransformedLearnerAndInviteeResponse,
  transformLearner,
} from './transformLearners';

export interface GetLearnersRequest extends QueryParamsBase {
  organisationId?: string | null;
  searchTerm?: string;
  searchMode?: SearchMode;
  query?: string;
}
export type UseGetLearnersResult = {
  status: QueryStatus;
  isFetched: boolean;
  paginator: Paginator<TransformedLearnerAndInviteeResponse>;
  apiPaginationProps?: ApiPaginationProps;
};
const useGetLearners = (
  searchTerm: string,
  searchMode: SearchMode,
  organisationId?: string | null,
): UseGetLearnersResult => {
  const minSearchCharacterLimit = getMinimumSearchCharacters(searchMode);

  const {
    data,
    isFetching,
    isFetched,
    status,
    hasNextPage: hasMore,
    fetchNextPage: loadMore,
  } = usePaginatedApi<
    PaginatedResponse<LearnerAndInviteeResponse>,
    LearnerAndInviteeResponse,
    DefaultPageParam & GetLearnersRequest
  >({
    fetchPage: getFilteredLearners,
    queryKey: [QUERY_KEY_ROOTS.LEARNERS_DATA, QUERY_KEY_ROOTS.PAGINATED_DATA],
    getItemsFromPage: (p) => p.response ?? [],
    initialPageParam: undefined,
    queryParams: {
      organisationId: organisationId ?? undefined,
      searchMode,
      searchTerm,
    },
    enabled: !!organisationId || searchTerm?.length >= minSearchCharacterLimit,
  });
  const { items = [], totalCount = 0 } = data ?? {};
  const paginator = usePaginationV2(
    items.map((item) => transformLearner(item)),
    DEFAULT_TABLE_ITEMS_PER_PAGE,
    totalCount,
  );

  return {
    paginator,
    isFetched,
    apiPaginationProps: {
      hasMoreToLoad: hasMore,
      loadMore,
      isFetching,
      loadingState: status,
    },
    status: queryStatusIgnoreIdle({
      status,
      isLoading: status === 'pending' && isFetching,
    }),
  };
};

export default useGetLearners;
